/* CNOIR Size & Fit
   - Pure recommendation engine (exported for tests; no DOM dependency)
   - <cnoir-size-fit> drawer (progressive enhancement over <details>)
   - Made-to-measure form controller: units, validation, review step
   Privacy: measurement/height/weight values never leave the browser and are
   never attached to analytics events. */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   *  Engine (pure functions)
   * ------------------------------------------------------------------ */

  var Engine = {
    round1: function (v) {
      return Math.round(v * 10) / 10;
    },
    toInches: function (value, unit) {
      if (typeof value !== 'number' || !isFinite(value)) return NaN;
      return unit === 'cm' ? value / 2.54 : value;
    },
    inToUnit: function (inches, unit) {
      return unit === 'cm' ? Engine.round1(inches * 2.54) : Engine.round1(inches);
    },
    parseNumber: function (raw) {
      if (raw == null) return NaN;
      var s = String(raw).trim().replace(',', '.');
      if (!/^\d+(\.\d+)?$/.test(s)) return NaN;
      return parseFloat(s);
    },

    /* Look a measurement up in chart rows.
       rows: [{size, uk, us, variant, values: {key: [min] | [min,max]}}]
       Returns {state, row?, confidence?, note?}
       Rules (deterministic, documented):
       - inside exactly one range        -> HIGH
       - on a boundary shared by two     -> lower size (rows order), HIGH
       - in a gap between two ranges     -> next size up, MEDIUM
       - below first / above last range  -> OUT_OF_RANGE (no extrapolation)   */
    findByMeasurement: function (rows, key, vIn) {
      if (!rows || !rows.length || !isFinite(vIn)) return { state: 'INVALID' };
      var usable = rows.filter(function (r) {
        return r.values && r.values[key] && r.values[key].length;
      });
      if (!usable.length) return { state: 'INVALID' };

      var matches = usable.filter(function (r) {
        var vals = r.values[key];
        var min = vals[0];
        var max = vals.length > 1 ? vals[vals.length - 1] : vals[0];
        return vIn >= min && vIn <= max;
      });

      if (matches.length >= 1) {
        return { state: 'MATCH', row: matches[0], confidence: 'HIGH' };
      }

      var first = usable[0].values[key];
      var last = usable[usable.length - 1].values[key];
      var firstMin = first[0];
      var lastMax = last.length > 1 ? last[last.length - 1] : last[0];

      if (vIn < firstMin) return { state: 'OUT_OF_RANGE', edge: 'below' };
      if (vIn > lastMax) return { state: 'OUT_OF_RANGE', edge: 'above' };

      for (var i = 0; i < usable.length; i++) {
        var min = usable[i].values[key][0];
        if (vIn < min) {
          return { state: 'BETWEEN', row: usable[i], confidence: 'MEDIUM' };
        }
      }
      return { state: 'OUT_OF_RANGE', edge: 'above' };
    },

    /* Height/weight engine. Fully implemented but returns DISABLED until a
       guide ships validated ranges: cfg = {enabled:true, ranges:[{height_cm:[min,max],
       weight_kg:[min,max], build?, size}]}. No fabricated defaults exist. */
    recommendHeightWeight: function (cfg, heightCm, weightKg, build) {
      if (!cfg || !cfg.enabled || !cfg.ranges || !cfg.ranges.length) {
        return { state: 'DISABLED' };
      }
      if (!isFinite(heightCm) || !isFinite(weightKg)) return { state: 'INVALID' };
      var b = build || 'REGULAR';
      var candidates = cfg.ranges.filter(function (r) {
        var okH = heightCm >= r.height_cm[0] && heightCm <= r.height_cm[1];
        var okW = weightKg >= r.weight_kg[0] && weightKg <= r.weight_kg[1];
        var okB = !r.build || r.build === b;
        return okH && okW && okB;
      });
      if (candidates.length === 1) {
        return { state: 'MATCH', size: candidates[0].size, confidence: 'HIGH' };
      }
      if (candidates.length > 1) {
        return {
          state: 'AMBIGUOUS',
          sizes: candidates.map(function (c) {
            return c.size;
          }),
          confidence: 'MEDIUM'
        };
      }
      return { state: 'OUT_OF_RANGE' };
    },

    /* Given product variants and option names, check whether a size value is
       purchasable with the currently selected non-size options. */
    variantForSize: function (variants, optionNames, currentOptions, sizeOptionIndex, sizeValue) {
      var wanted = currentOptions.slice();
      wanted[sizeOptionIndex] = sizeValue;
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options;
        var all = true;
        for (var j = 0; j < optionNames.length; j++) {
          if (wanted[j] != null && opts[j] !== wanted[j]) {
            all = false;
            break;
          }
        }
        if (all) return variants[i];
      }
      return null;
    }
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Engine;
  }
  if (typeof window === 'undefined') return;
  if (window.CnoirSizeFitEngine) return; // script included twice — bail
  window.CnoirSizeFitEngine = Engine;

  /* ------------------------------------------------------------------ *
   *  Shared helpers
   * ------------------------------------------------------------------ */

  function emit(name, data) {
    try {
      var detail = Object.assign({ event: name }, data || {});
      document.dispatchEvent(new CustomEvent('cnoir:size-fit', { detail: detail }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
    } catch (e) {
      /* analytics must never break the store */
    }
  }

  function focusTrap(el, target) {
    if (typeof window.trapFocus === 'function') window.trapFocus(el, target);
    else if (target) target.focus();
  }
  function focusRelease(returnTo) {
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(returnTo);
    else if (returnTo) returnTo.focus();
  }

  function setSegActive(group, btn) {
    group.querySelectorAll('.cnoir-sf-seg__btn').forEach(function (b) {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });
  }

  function formatCells(scope, unit) {
    scope.querySelectorAll('[data-sf-in]').forEach(function (td) {
      var vals = td
        .getAttribute('data-sf-in')
        .split(',')
        .filter(function (s) {
          return s !== '';
        })
        .map(Number);
      if (!vals.length) return;
      var out = vals.map(function (v) {
        return unit === 'cm' ? Math.round(v * 2.54) : v;
      });
      if (td.hasAttribute('data-sf-list')) td.textContent = out.join(' / ');
      else if (out.length === 2) td.textContent = out[0] + '–' + out[1];
      else td.textContent = out[0];
    });
    scope.querySelectorAll('[data-sf-unit-label]').forEach(function (el) {
      el.textContent = unit;
    });
  }

  /* Wire a chart block (used in drawer and on the standalone page). */
  function initChart(chart) {
    if (chart.dataset.sfChartInit) return;
    chart.dataset.sfChartInit = '1';

    var unitsSeg = chart.querySelector('[data-sf-units]');
    if (unitsSeg) {
      unitsSeg.hidden = false;
      unitsSeg.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-sf-unit]');
        if (!btn) return;
        setSegActive(unitsSeg, btn);
        formatCells(chart, btn.getAttribute('data-sf-unit'));
      });
    }

    var tableSwitch = chart.querySelector('[data-sf-table-switch]');
    if (tableSwitch) {
      tableSwitch.hidden = false;
      var wraps = chart.querySelectorAll('[data-sf-table]');
      var headings = chart.querySelectorAll('[data-sf-table-heading]');
      headings.forEach(function (h) {
        h.hidden = true;
      });
      var show = function (idx) {
        wraps.forEach(function (w) {
          w.hidden = w.getAttribute('data-sf-table') !== String(idx);
        });
      };
      show(0);
      tableSwitch.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-sf-table-btn]');
        if (!btn) return;
        setSegActive(tableSwitch, btn);
        show(btn.getAttribute('data-sf-table-btn'));
      });
      chart._sfShowTable = show;
      chart._sfTableSwitch = tableSwitch;
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.cnoir-sf-page [data-sf-chart]').forEach(initChart);
  });

  /* ------------------------------------------------------------------ *
   *  Drawer custom element
   *
   *  The drawer panel is moved into a native modal <dialog> appended to
   *  <body>. A modal dialog renders in the browser's top layer, so no
   *  sticky header, sticky product column or z-index elsewhere in the
   *  theme can ever cover it or its Close button. The dialog also gives
   *  us, natively: Escape to close from anywhere on the page, a backdrop,
   *  an inert page behind, and focus return to the trigger.
   *
   *  Ways to dismiss (all call close()): the labelled Close button,
   *  Escape, a click on the backdrop, or the trigger itself.
   * ------------------------------------------------------------------ */

  var UNIT_LIMITS_IN = { min: 10, max: 80 };
  var CLOSE_MS = 330;
  var SUPPORTS_DIALOG =
    typeof window.HTMLDialogElement !== 'undefined' &&
    typeof window.HTMLDialogElement.prototype.showModal === 'function';

  class CnoirSizeFit extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sfInit) {
        /* re-attached after a move: restore the dialog we removed on disconnect */
        if (this.dialog && !this.dialog.isConnected) document.body.appendChild(this.dialog);
        return;
      }
      this.dataset.sfInit = '1';

      try {
        this.data = JSON.parse(this.querySelector('[data-sf-data]').textContent);
      } catch (e) {
        this.data = null;
      }

      this.details = this.querySelector('[data-sf-details]');
      this.trigger = this.querySelector('[data-sf-trigger]');
      this.panel = this.querySelector('[data-sf-panel]');
      this.closeBtn = this.querySelector('[data-sf-close]');
      if (!this.details || !this.trigger || !this.panel) return;

      /* No <dialog> support: leave the native <details> disclosure untouched.
         It opens and closes inline from the trigger, so it can never trap anyone. */
      if (!SUPPORTS_DIALOG) return;

      /* Enhance: details -> modal dialog */
      this.classList.add('is-enhanced');
      this.trigger.setAttribute('role', 'button');
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.closeBtn) this.closeBtn.hidden = false;

      this.safeContext = {
        product_id: this.dataset.productId,
        guide: this.dataset.guide,
        guide_version: this.dataset.version,
        garment_type: this.dataset.garmentType,
        mode: this.dataset.mode
      };

      this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          this.isOpen() ? this.close() : this.open();
        }
      });
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      }

      /* Wire the panel's contents while it is still inside this element. */
      this.initTabs();
      this.querySelectorAll('[data-sf-chart]').forEach(initChart);
      this.initFinder();

      var support = this.querySelector('[data-sf-support]');
      if (support) {
        support.addEventListener('click', () => emit('sizing_support_clicked', this.safeContext));
      }

      this.buildDialog();
    }

    disconnectedCallback() {
      if (!this.dialog) return;
      window.clearTimeout(this._closeTimer);
      if (this.dialog.open) this.dialog.close();
      this.onDialogClosed();
      this.dialog.remove();
    }

    buildDialog() {
      var dialog = document.createElement('dialog');
      dialog.className = 'cnoir-sf cnoir-sf-dialog is-enhanced';
      /* carry the section colour scheme so the panel keeps its colours outside the section */
      var scheme = this.closest('[class*="color-"]');
      if (scheme) {
        scheme.classList.forEach(function (c) {
          if (c.indexOf('color-') === 0) dialog.classList.add(c);
        });
      }
      dialog.setAttribute('aria-label', this.panel.getAttribute('aria-label') || 'Size and fit');
      this.panel.removeAttribute('aria-label');
      dialog.appendChild(this.panel);
      document.body.appendChild(dialog);
      this.dialog = dialog;

      /* Escape (fires wherever focus is, because the dialog is modal) */
      dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      /* Backdrop: only when the press both started and ended outside the panel */
      var pressedOnBackdrop = false;
      dialog.addEventListener('pointerdown', function (e) {
        pressedOnBackdrop = e.target === dialog;
      });
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog && pressedOnBackdrop) this.close();
        pressedOnBackdrop = false;
      });
      /* Whatever closes the dialog (including the browser itself), leave the page usable. */
      dialog.addEventListener('close', this.onDialogClosed.bind(this));
    }

    isOpen() {
      return !!this.dialog && this.dialog.classList.contains('is-open');
    }

    onTriggerClick(e) {
      e.preventDefault();
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      if (!this.dialog || this.isOpen()) return;
      window.clearTimeout(this._closeTimer);
      try {
        if (!this.dialog.open) this.dialog.showModal();
      } catch (e) {
        return; /* never leave the page locked if the dialog cannot open */
      }
      void this.panel.offsetWidth; /* reflow so the drawer transition runs even in throttled tabs */
      this.dialog.classList.add('is-open');
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('cnoir-sf-lock');
      var first = this.closeBtn || this.panel;
      try {
        first.focus({ preventScroll: true });
      } catch (e) {
        first.focus();
      }
      emit('size_fit_opened', this.safeContext);
    }

    close() {
      if (!this.dialog || !this.isOpen()) return;
      this.dialog.classList.remove('is-open');
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      /* the page is scrollable again immediately, not after the animation */
      document.documentElement.classList.remove('cnoir-sf-lock');
      var dialog = this.dialog;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.clearTimeout(this._closeTimer);
      this._closeTimer = window.setTimeout(
        function () {
          if (dialog.open) dialog.close();
        },
        reduce ? 0 : CLOSE_MS
      );
      emit('size_fit_closed', this.safeContext);
    }

    onDialogClosed() {
      if (this.dialog) this.dialog.classList.remove('is-open');
      this.classList.remove('is-open');
      document.documentElement.classList.remove('cnoir-sf-lock');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    }

    /* -------- tabs -------- */

    initTabs() {
      var tablist = this.querySelector('[data-sf-tablist]');
      if (!tablist) return;
      tablist.hidden = false;
      var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
      var panels = this.querySelectorAll('[data-sf-tabpanel]');
      var self = this;

      function activate(tab, focus) {
        tabs.forEach(function (t) {
          var active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.tabIndex = active ? 0 : -1;
        });
        panels.forEach(function (p) {
          p.hidden = p.getAttribute('data-sf-tabpanel') !== tab.getAttribute('data-sf-tab');
        });
        if (focus) tab.focus();
        emit(
          tab.getAttribute('data-sf-tab') === 'chart' ? 'size_chart_viewed' : 'how_to_measure_viewed',
          self.safeContext
        );
      }

      panels.forEach(function (p) {
        p.hidden = p.getAttribute('data-sf-tabpanel') !== 'chart';
      });

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          activate(tab, false);
        });
        tab.addEventListener('keydown', function (e) {
          var idx = null;
          if (e.key === 'ArrowRight') idx = (i + 1) % tabs.length;
          else if (e.key === 'ArrowLeft') idx = (i - 1 + tabs.length) % tabs.length;
          else if (e.key === 'Home') idx = 0;
          else if (e.key === 'End') idx = tabs.length - 1;
          if (idx !== null) {
            e.preventDefault();
            activate(tabs[idx], true);
          }
        });
      });
    }

    /* -------- finder -------- */

    initFinder() {
      var finder = this.querySelector('[data-sf-finder]');
      if (!finder || !this.data || !this.data.guide || !this.data.guide.chart) return;
      var reco = this.data.guide.recommendation || {};
      var check = reco.measurement_check || {};
      var hw = reco.height_weight || {};
      /* Only show the finder when a supported input mode is available.
         Height/weight UI stays off until validated ranges exist. */
      if (!check.enabled && !(hw.enabled && hw.ranges && hw.ranges.length)) return;

      finder.hidden = false;
      this.finder = finder;
      this.finderUnit = 'in';
      this.activeTableIndex = 0;

      var self = this;
      var tables = this.data.guide.chart.tables;

      var tableSeg = finder.querySelector('[data-sf-finder-table]');
      if (tableSeg) {
        tableSeg.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-sf-finder-table-btn]');
          if (!btn) return;
          setSegActive(tableSeg, btn);
          self.activeTableIndex = Number(btn.getAttribute('data-sf-finder-table-btn'));
          self.updateFinderQuestion();
          /* keep the chart below in step with the audience choice
             (query the panel: it lives in the dialog, not inside this element) */
          var chart = self.panel.querySelector('[data-sf-chart]');
          if (chart && chart._sfShowTable) {
            chart._sfShowTable(self.activeTableIndex);
            var btn2 = chart._sfTableSwitch.querySelector(
              '[data-sf-table-btn="' + self.activeTableIndex + '"]'
            );
            if (btn2) setSegActive(chart._sfTableSwitch, btn2);
          }
        });
      }

      var unitSeg = finder.querySelector('[data-sf-finder-units]');
      if (unitSeg) {
        unitSeg.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-sf-unit]');
          if (!btn) return;
          setSegActive(unitSeg, btn);
          self.finderUnit = btn.getAttribute('data-sf-unit');
        });
      }

      var findBtn = finder.querySelector('[data-sf-find]');
      if (findBtn) {
        findBtn.addEventListener('click', function () {
          emit('size_recommendation_started', self.safeContext);
          if (check.type === 'variant_direct') self.runDirectFinder();
          else self.runMeasureFinder();
        });
      }
      this.updateFinderQuestion();
    }

    measureKeyFor(tableIndex) {
      var reco = this.data.guide.recommendation || {};
      var check = reco.measurement_check || {};
      var table = this.data.guide.chart.tables[tableIndex];
      if (check.per_table && check.per_table[table.label]) return check.per_table[table.label];
      return table.measure_key || (table.columns[0] && table.columns[0].key);
    }

    updateFinderQuestion() {
      var q = this.finder && this.finder.querySelector('[data-sf-question]');
      if (!q) return;
      var table = this.data.guide.chart.tables[this.activeTableIndex];
      var key = this.measureKeyFor(this.activeTableIndex);
      var col = (table.columns || []).filter(function (c) {
        return c.key === key;
      })[0];
      if (col) q.textContent = 'What is your ' + col.label.toLowerCase() + ' measurement?';
    }

    runMeasureFinder() {
      var input = this.finder.querySelector('[data-sf-measure-input]');
      var errEl = this.finder.querySelector('[data-sf-finder-error]');
      var resultEl = this.finder.querySelector('[data-sf-result]');
      if (!input || !resultEl) return;
      errEl.hidden = true;
      input.removeAttribute('aria-invalid');

      var v = Engine.parseNumber(input.value);
      var vIn = Engine.toInches(v, this.finderUnit);
      if (!isFinite(vIn) || vIn <= 0) {
        errEl.textContent = 'Please enter your measurement as a number, e.g. 40.';
        errEl.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      if (vIn < UNIT_LIMITS_IN.min || vIn > UNIT_LIMITS_IN.max) {
        errEl.textContent =
          'That doesn’t look like a ' +
          (this.finderUnit === 'cm' ? 'centimetre' : 'inch') +
          ' measurement we can work with — please double-check the number and unit.';
        errEl.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }

      var table = this.data.guide.chart.tables[this.activeTableIndex];
      var key = this.measureKeyFor(this.activeTableIndex);
      var res = Engine.findByMeasurement(table.rows, key, vIn);
      this.renderResult(res, table, key);
    }

    runDirectFinder() {
      var w = this.finder.querySelector('[data-sf-direct-primary]');
      var i = this.finder.querySelector('[data-sf-direct-secondary]');
      var resultEl = this.finder.querySelector('[data-sf-result]');
      if (!w || !i || !resultEl) return;
      if (!w.value || !i.value) {
        resultEl.innerHTML = '';
        var p = document.createElement('p');
        p.className = 'cnoir-sf__error';
        p.textContent = 'Please choose both your waist and inseam.';
        resultEl.appendChild(p);
        return;
      }
      var target = (w.value + '×' + i.value).replace(/\s/g, '');
      var sizeName = (this.data.guide.recommendation.measurement_check || {}).option_name || 'Size';
      var optIdx = this.data.product.optionNames.indexOf(sizeName);
      var match = null;
      if (optIdx > -1) {
        this.data.product.variants.forEach(function (variant) {
          var val = (variant.options[optIdx] || '').replace(/\s/g, '');
          if (val === target) match = variant.options[optIdx];
        });
      }
      if (!match) {
        this.renderResult({ state: 'NO_VARIANT', label: w.value + ' × ' + i.value }, null, null);
        return;
      }
      this.renderResult({ state: 'MATCH', direct: match, confidence: 'HIGH' }, null, null);
    }

    currentPickerOptions() {
      var names = this.data.product.optionNames;
      var out = [];
      var section = this.closest('section') || document;
      names.forEach(function (name, idx) {
        var sel = section.querySelector('select[name="options[' + name + ']"]');
        if (sel) {
          out[idx] = sel.value;
          return;
        }
        var radio = section.querySelector(
          'variant-selects input[type="radio"][name^="' + name + '-"]:checked, variant-selects input[type="radio"][name="' + name + '"]:checked'
        );
        out[idx] = radio ? radio.value : null;
      });
      return out;
    }

    renderResult(res, table, key) {
      var resultEl = this.finder.querySelector('[data-sf-result]');
      resultEl.innerHTML = '';
      var card = document.createElement('div');
      card.className = 'cnoir-sf__result-card';
      var mode = this.dataset.mode;
      var isMtm = mode === 'MADE_TO_MEASURE';
      var self = this;

      function el(tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text) n.textContent = text;
        return n;
      }

      if (res.state === 'OUT_OF_RANGE') {
        card.appendChild(el('p', 'cnoir-sf__result-eyebrow', 'Your fit needs a closer check'));
        card.appendChild(
          el(
            'p',
            'cnoir-sf__result-note',
            'Your measurement sits outside the range this chart covers, so we won’t guess. Use the measurement guide below or contact our team — we’ll help you find the right fit.'
          )
        );
        resultEl.appendChild(card);
        emit('size_recommendation_needs_more_info', this.safeContext);
        return;
      }
      if (res.state === 'INVALID') {
        card.appendChild(el('p', 'cnoir-sf__result-note', 'We couldn’t check that — please try again.'));
        resultEl.appendChild(card);
        return;
      }
      if (res.state === 'NO_VARIANT') {
        card.appendChild(el('p', 'cnoir-sf__result-eyebrow', 'Not available in this piece'));
        card.appendChild(
          el(
            'p',
            'cnoir-sf__result-note',
            res.label +
              ' isn’t offered for this product. Compare the chart below, or contact our team for help.'
          )
        );
        resultEl.appendChild(card);
        emit('size_recommendation_completed', Object.assign({ result_state: 'no_variant' }, this.safeContext));
        return;
      }

      var sizeLabel = res.direct || (res.row && res.row.size);
      var eyebrow = isMtm ? 'Your reference size' : res.state === 'BETWEEN' ? 'Most likely size' : 'We recommend';
      card.appendChild(el('p', 'cnoir-sf__result-eyebrow', eyebrow));
      card.appendChild(el('p', 'cnoir-sf__result-size', sizeLabel));

      if (res.row && (res.row.uk !== res.row.size || res.row.us !== res.row.size)) {
        card.appendChild(el('p', 'cnoir-sf__result-conv', 'UK ' + res.row.uk + ' / US ' + res.row.us));
      }
      if (res.row && table && key) {
        var vals = res.row.values[key];
        var colLabel = (table.columns.filter(function (c) {
          return c.key === key;
        })[0] || {}).label;
        if (vals && colLabel) {
          var range = vals.length === 2 ? vals[0] + '–' + vals[1] : vals[0];
          card.appendChild(el('p', 'cnoir-sf__result-conv', colLabel + ' ' + range + ' in'));
        }
      }
      var fitNote = this.panel.querySelector('.cnoir-sf__fit-note');
      if (fitNote) card.appendChild(el('p', 'cnoir-sf__result-note', fitNote.textContent));
      if (res.state === 'BETWEEN') {
        card.appendChild(
          el(
            'p',
            'cnoir-sf__result-note',
            'Your measurement falls between sizes — ' +
              sizeLabel +
              ' is the next size up. Compare both rows in the size chart below before you decide.'
          )
        );
      }

      if (isMtm) {
        card.appendChild(
          el('p', 'cnoir-sf__result-note', 'This piece is made using the measurements submitted with your order.')
        );
        var mtmTarget = document.querySelector('[data-cnoir-mtm]');
        if (mtmTarget) {
          var btnGo = el('button', 'cnoir-sf__button', 'Continue to measurements');
          btnGo.type = 'button';
          btnGo.addEventListener('click', function () {
            self.close();
            window.setTimeout(function () {
              mtmTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
              var firstField = mtmTarget.querySelector('[data-mtm-field]');
              if (firstField) firstField.focus({ preventScroll: true });
            }, CLOSE_MS + 60);
          });
          card.appendChild(btnGo);
        }
      } else {
        var variantValue = res.direct || (res.row && res.row.variant);
        if (!variantValue) {
          card.appendChild(
            el('p', 'cnoir-sf__result-note', 'This size isn’t offered in this piece — contact our team for options.')
          );
        } else {
          var sizeName =
            ((this.data.guide.recommendation || {}).measurement_check || {}).option_name || 'Size';
          var optIdx = this.data.product.optionNames.indexOf(sizeName);
          if (optIdx > -1) {
            var current = this.currentPickerOptions();
            var variant = Engine.variantForSize(
              this.data.product.variants,
              this.data.product.optionNames,
              current,
              optIdx,
              variantValue
            );
            if (variant && variant.available) {
              var btn = el('button', 'cnoir-sf__button', 'Select ' + variantValue);
              btn.type = 'button';
              btn.addEventListener('click', function () {
                self.selectSize(sizeName, optIdx, variantValue);
              });
              card.appendChild(btn);
            } else {
              card.appendChild(
                el(
                  'p',
                  'cnoir-sf__result-note cnoir-sf__result-note--warn',
                  variantValue +
                    ' is currently unavailable in this combination. We won’t suggest a different size — fit comes first. Try another colour or material, or contact our team.'
                )
              );
            }
          }
        }
      }

      resultEl.appendChild(card);
      emit(
        'size_recommendation_completed',
        Object.assign(
          { result_state: res.state.toLowerCase(), confidence: res.confidence || null, size: sizeLabel },
          this.safeContext
        )
      );
    }

    selectSize(optionName, optionIndex, value) {
      var section = this.closest('section') || document;
      var applied = false;
      var sel = section.querySelector('select[name="options[' + optionName + ']"]');
      if (sel) {
        sel.value = value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        applied = sel.value === value;
      } else {
        var radios = section.querySelectorAll(
          'variant-selects input[type="radio"][name^="' + optionName + '-"], variant-selects input[type="radio"][name="' + optionName + '"]'
        );
        radios.forEach(function (r) {
          if (r.value === value && !applied) {
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
            applied = true;
          }
        });
      }
      if (applied) {
        emit('recommended_variant_selected', Object.assign({ size: value }, this.safeContext));
        this.close();
      }
    }
  }

  if (!customElements.get('cnoir-size-fit')) {
    customElements.define('cnoir-size-fit', CnoirSizeFit);
  }

  /* ------------------------------------------------------------------ *
   *  Made-to-measure form
   * ------------------------------------------------------------------ */

  function initMtm(container) {
    if (container.dataset.mtmInit) return;
    container.dataset.mtmInit = '1';

    var form = container.closest('form');
    var unit = 'in';
    var unitsInput = container.querySelector('[data-mtm-units-input]');
    var unitSeg = container.querySelector('[data-mtm-units]');
    var fields = Array.prototype.slice.call(container.querySelectorAll('[data-mtm-field]'));
    var confirmed = false;

    var safeContext = {
      guide: container.dataset.guide,
      guide_version: container.dataset.version
    };

    function limits(field) {
      var minIn = parseFloat(field.getAttribute('data-min-in'));
      var maxIn = parseFloat(field.getAttribute('data-max-in'));
      return {
        min: Engine.inToUnit(minIn, unit),
        max: Engine.inToUnit(maxIn, unit)
      };
    }

    if (unitSeg) {
      unitSeg.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-sf-unit]');
        if (!btn) return;
        var next = btn.getAttribute('data-sf-unit');
        if (next === unit) return;
        setSegActive(unitSeg, btn);
        /* convert anything already typed so the submitted numbers always match _Units */
        fields.forEach(function (f) {
          var v = Engine.parseNumber(f.value);
          if (isFinite(v)) {
            f.value = String(next === 'cm' ? Engine.round1(v * 2.54) : Engine.round1(v / 2.54));
          }
          var ex = parseFloat(f.getAttribute('data-example-in'));
          if (isFinite(ex)) f.placeholder = 'e.g. ' + Engine.inToUnit(ex, next);
        });
        unit = next;
        if (unitsInput) unitsInput.value = unit;
      });
    }

    function validate() {
      var firstBad = null;
      fields.forEach(function (f) {
        var err = f.parentElement.querySelector('[data-mtm-error]');
        var v = Engine.parseNumber(f.value);
        var lim = limits(f);
        var bad = false;
        if (f.required && f.value.trim() === '') {
          bad = true;
          if (err) err.textContent = 'Please enter this measurement.';
        } else if (f.value.trim() !== '' && !isFinite(v)) {
          bad = true;
          if (err) err.textContent = 'Please enter a number, e.g. ' + f.placeholder.replace('e.g. ', '');
        } else if (f.value.trim() !== '' && (v < lim.min || v > lim.max)) {
          bad = true;
          if (err)
            err.textContent =
              'Please double-check — we expected between ' + lim.min + ' and ' + lim.max + ' ' + unit + '.';
        }
        if (err) err.hidden = !bad;
        if (bad) {
          f.setAttribute('aria-invalid', 'true');
          if (!firstBad) firstBad = f;
        } else {
          f.removeAttribute('aria-invalid');
        }
      });
      return firstBad;
    }

    function openReview(onConfirm) {
      var review = document.createElement('div');
      review.className = 'cnoir-sf-review';
      review.innerHTML =
        '<div class="cnoir-sf-review__overlay" data-review-cancel></div>' +
        '<div class="cnoir-sf-review__card" role="dialog" aria-modal="true" aria-label="Check your measurements" tabindex="-1">' +
        '<h2 class="cnoir-sf-review__title">Check your measurements</h2>' +
        '<p class="cnoir-sf-review__sub">Your piece is crafted to these exact measurements — a ten-second check now prevents a remake later.</p>' +
        '<dl class="cnoir-sf-review__list"></dl>' +
        '<div class="cnoir-sf-review__actions">' +
        '<button type="button" class="cnoir-sf__button cnoir-sf__button--ghost" data-review-cancel-btn>Edit</button>' +
        '<button type="button" class="cnoir-sf__button" data-review-confirm>Confirm</button>' +
        '</div></div>';
      var list = review.querySelector('.cnoir-sf-review__list');
      fields.forEach(function (f) {
        if (f.value.trim() === '') return;
        var row = document.createElement('div');
        row.className = 'cnoir-sf-review__row';
        var dt = document.createElement('dt');
        dt.textContent = f.getAttribute('data-label');
        var dd = document.createElement('dd');
        dd.textContent = f.value.trim() + ' ' + unit;
        row.appendChild(dt);
        row.appendChild(dd);
        list.appendChild(row);
      });
      document.body.appendChild(review);
      var card = review.querySelector('[role="dialog"]');
      focusTrap(card, review.querySelector('[data-review-confirm]'));
      emit('size_fit_mtm_review_opened', safeContext);

      function closeReview(focusEl) {
        focusRelease(focusEl || fields[0]);
        review.remove();
        document.removeEventListener('keydown', onKey);
      }
      function onKey(e) {
        if (e.key === 'Escape') closeReview();
      }
      document.addEventListener('keydown', onKey);
      review.querySelector('[data-review-cancel]').addEventListener('click', function () {
        closeReview();
      });
      review.querySelector('[data-review-cancel-btn]').addEventListener('click', function () {
        closeReview();
      });
      review.querySelector('[data-review-confirm]').addEventListener('click', function () {
        emit('size_fit_mtm_review_confirmed', safeContext);
        closeReview();
        onConfirm();
      });
    }

    function gate(e, resume) {
      /* Anything unexpected -> fail open so purchasing is never blocked. */
      try {
        if (confirmed) {
          confirmed = false;
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        var bad = validate();
        if (bad) {
          bad.focus();
          bad.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        openReview(function () {
          confirmed = true;
          resume();
        });
      } catch (err) {
        confirmed = false;
      }
    }

    if (form) {
      /* Gate on the buy-button clicks (capture) so Dawn's own submit handling —
         AJAX or native — runs completely untouched after Confirm. */
      form.addEventListener(
        'click',
        function (e) {
          var target = e.target.closest('button[name="add"], .shopify-payment-button button, .shopify-payment-button');
          if (!target) return;
          gate(e, function () {
            target.click();
          });
        },
        true
      );

      /* Enter inside a measurement field must not skip the review step. */
      container.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target.matches('[data-mtm-field], .cnoir-mtm__input')) {
          e.preventDefault();
          var addBtn = form.querySelector('button[name="add"]');
          if (addBtn) addBtn.click();
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-cnoir-mtm]').forEach(initMtm);
  });
})();
